import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Copy, Trash2, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi, assetUrl, uploadFiles, errMsg } from "@/lib/api";
import { ConfirmDialog } from "@/admin/ConfirmDialog";

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const load = () => adminApi.get("/admin/media").then((r) => setFiles(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const upload = async (list) => {
    if (!list?.length) return;
    setBusy(true);
    try {
      await uploadFiles(list);
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed – select the link manually"); }
  };

  const remove = async () => {
    try { await adminApi.delete(`/admin/media/${deleting.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDeleting(null);
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-bold text-charcoal">Media Library</h1><p className="text-sm text-muted-foreground">Upload images or PDFs and copy their permanent public link.</p></div>
        <label className="inline-flex items-center gap-2 rounded-md bg-brand hover:bg-brand-dark text-white px-4 py-2 text-sm font-semibold cursor-pointer" data-testid="media-upload-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Upload files
          <input type="file" multiple accept="image/*,application/pdf" hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="media-upload-input" />
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {files.map((f) => {
          const url = assetUrl(f.url);
          return (
            <div key={f.id} className="rounded-xl border bg-white p-3 space-y-2" data-testid={`media-item-${f.id}`}>
              <div className="h-36 rounded-lg bg-muted grid place-items-center overflow-hidden">
                {f.content_type.startsWith("image/") ? <img src={url} alt={f.filename} className="max-h-36 w-auto object-contain" /> : <FileText className="h-12 w-12 text-brand" />}
              </div>
              <p className="text-xs font-semibold truncate" title={f.filename}>{f.filename}</p>
              <Input readOnly value={url} onFocus={(e) => e.target.select()} className="text-[11px] h-8" data-testid={`media-link-${f.id}`} />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => copy(url)} data-testid={`media-copy-${f.id}`}><Copy className="h-3.5 w-3.5 mr-1" />Copy link</Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleting(f)} className="text-destructive" data-testid={`media-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          );
        })}
      </div>
      {!files.length && <p className="text-sm text-muted-foreground py-16 text-center" data-testid="media-empty">No files uploaded yet.</p>}
      <ConfirmDialog open={!!deleting} title="Delete file?" description="Anywhere this link is used will stop showing the file." onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
}
